import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { normalizeMessage } from '../utils/message-layout';
import { Board, BoardStatus } from '../models/board.model';

/**
 * Backend board response format (from Go API - authenticated endpoints)
 */
interface BackendBoard {
  id: number;
  public_id: string;
  name: string;
  display_title?: string | null;
  num_rows: number;
  num_columns: number;
  message_update_interval: number;
  has_passcode: boolean;
  sound_enabled?: boolean;
  messages: BackendMessage[];
  created_at: string;
  updated_at: string;
}

/**
 * Backend public board response format (from Go API - public display endpoint)
 */
interface BackendPublicBoard {
  id: number;
  public_id: string;
  display_title?: string | null;
  num_rows: number;
  num_columns: number;
  message_update_interval: number;
  has_passcode: boolean;
  sound_enabled?: boolean;
  messages: BackendMessage[];
}

/**
 * Backend message format (from Go API)
 */
interface BackendMessage {
  id?: string;
  align?: 'left' | 'center' | 'right';
  accent_color?: string | null;
  layout?: 'lines';
  sequence: number;
  text: string;
  expire_at?: string | null;
}

@Injectable({
  providedIn: 'root',
})
export class BoardApiService {
  private http = inject(HttpClient);

  /**
   * Get all boards for the authenticated user
   */
  getBoards(): Observable<Board[]> {
    return this.http
      .get<BackendBoard[]>('/api/app/boards')
      .pipe(map((boards) => boards.map((board) => this.mapBackendToFrontend(board))));
  }

  /**
   * Get a single board by ID
   */
  getBoard(id: string): Observable<Board> {
    return this.http
      .get<BackendBoard>(`/api/app/boards/${id}`)
      .pipe(map((board) => this.mapBackendToFrontend(board)));
  }

  /**
   * Create a new board
   */
  createBoard(board: Partial<Board>): Observable<Board> {
    const request = this.mapFrontendToBackend(board);
    return this.http
      .post<BackendBoard>('/api/app/boards', request)
      .pipe(map((board) => this.mapBackendToFrontend(board)));
  }

  /**
   * Update an existing board
   */
  updateBoard(id: string, board: Partial<Board>): Observable<Board> {
    const request = this.mapFrontendToBackend(board);
    return this.http
      .put<BackendBoard>(`/api/app/boards/${id}`, request)
      .pipe(map((board) => this.mapBackendToFrontend(board)));
  }

  /**
   * Delete a board
   */
  deleteBoard(id: string): Observable<void> {
    return this.http.delete<void>(`/api/app/boards/${id}`);
  }

  /**
   * Hard-delete all data owned by the authenticated user (account deletion).
   * Called after the auth account is deleted; relies on the cached JWT.
   */
  deleteAccountData(): Observable<void> {
    return this.http.delete<void>('/api/app/user');
  }

  /**
   * Get a public board by public ID (with optional passcode in header)
   * This endpoint is accessible without authentication if passcode is correct
   * @param publicId The 6-character public board identifier
   * @param passcode Optional passcode for protected boards
   */
  getPublicBoard(publicId: string, passcode?: string): Observable<Board> {
    // Build headers object - only include passcode if provided
    const headers: Record<string, string> = {};
    if (passcode) {
      headers['X-Board-Passcode'] = passcode;
    }

    return this.http
      .get<BackendPublicBoard>(`/api/display/${publicId}`, { headers })
      .pipe(map((board) => this.mapPublicBoardToFrontend(board)));
  }

  getDisplayState(id: string): Observable<{ message_id: string | null }> {
    return this.http.get<{ message_id: string | null }>(`/api/app/boards/${id}/display-state`);
  }

  reportDisplay(publicId: string, messageId: string | null, passcode?: string) {
    return this.http.post(
      `/api/display/${publicId}/state`,
      { message_id: messageId },
      {
        headers: passcode ? { 'X-Board-Passcode': passcode } : {},
      },
    );
  }

  reportOwnerDisplay(id: string, messageId: string | null) {
    return this.http.post(`/api/app/boards/${id}/display-state`, { message_id: messageId });
  }

  /**
   * Map backend board format to frontend Board model (authenticated endpoints)
   */
  private mapBackendToFrontend(backend: BackendBoard): Board {
    // Determine status based on message count and passcode
    // (Backend doesn't have status, so we infer it)
    let status: BoardStatus = 'active';
    if (backend.messages.length === 0) {
      status = 'draft';
    }

    return {
      id: backend.id.toString(),
      public_id: backend.public_id,
      name: backend.name,
      title: backend.display_title || undefined,
      status,
      config: {
        rows: backend.num_rows,
        columns: backend.num_columns,
        rotationInterval: backend.message_update_interval,
      },
      soundEnabled: backend.sound_enabled ?? true,
      messages: backend.messages
        .map((msg) =>
          normalizeMessage(
            {
              id: msg.id || `${backend.id}-${msg.sequence}`,
              text: msg.text,
              expiresAt: msg.expire_at || undefined,
              order: msg.sequence,
              align: msg.align,
              accentColor: msg.accent_color,
              layout: msg.layout,
            },
            backend.num_columns,
          ),
        )
        .sort((a, b) => a.order - b.order),
      passcode: backend.has_passcode ? '***' : undefined, // Never expose actual passcode
      hasPasscode: backend.has_passcode,
      viewCount: 0, // Backend doesn't track this yet, default to 0
      createdAt: backend.created_at,
      updatedAt: backend.updated_at,
    };
  }

  /**
   * Map backend public board format to frontend Board model (public display endpoint)
   */
  private mapPublicBoardToFrontend(backend: BackendPublicBoard): Board {
    const now = new Date().toISOString();

    return {
      id: backend.id.toString(),
      public_id: backend.public_id,
      name: '', // Not available in public response
      title: backend.display_title || undefined,
      status: 'active', // Public boards are always active
      config: {
        rows: backend.num_rows,
        columns: backend.num_columns,
        rotationInterval: backend.message_update_interval,
      },
      soundEnabled: backend.sound_enabled ?? true,
      messages: backend.messages
        .map((msg) =>
          normalizeMessage(
            {
              id: msg.id || `${backend.id}-${msg.sequence}`,
              text: msg.text,
              expiresAt: msg.expire_at || undefined,
              order: msg.sequence,
              align: msg.align,
              accentColor: msg.accent_color,
              layout: msg.layout,
            },
            backend.num_columns,
          ),
        )
        .sort((a, b) => a.order - b.order),
      passcode: backend.has_passcode ? '***' : undefined,
      hasPasscode: backend.has_passcode,
      viewCount: 0,
      createdAt: now, // Not available in public response
      updatedAt: now, // Not available in public response
    };
  }

  /**
   * Map frontend Board model to backend request format
   */
  private mapFrontendToBackend(frontend: Partial<Board>): unknown {
    // Ensure rotation interval meets backend validation (min 5 seconds)
    const rotationInterval = frontend.config?.rotationInterval;
    const validRotationInterval = rotationInterval && rotationInterval >= 5 ? rotationInterval : 10;

    return {
      name: frontend.name,
      sound_enabled: frontend.soundEnabled ?? true,
      display_title: frontend.title || null,
      num_rows: frontend.config?.rows,
      num_columns: frontend.config?.columns,
      message_update_interval: validRotationInterval,
      passcode: frontend.passcode && frontend.passcode !== '***' ? frontend.passcode : null,
      messages:
        frontend.messages?.map((msg) => ({
          id: msg.id,
          align: msg.align || 'left',
          accent_color: msg.accentColor || null,
          layout: 'lines',
          sequence: msg.order,
          text: msg.text,
          expire_at: msg.expiresAt || null,
        })) || [],
    };
  }
}
